import { type ReactNode, useEffect, useRef } from 'react'
import { type Observable } from 'rxjs'
import AutosizeTextarea from 'react-textarea-autosize'
import cx from 'classnames'
import { Button, RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import {
  ModalSheet,
  ModalSheetHeader,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentTitle,
  ModalSheetContentDescription,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
} from '@/components/ModalSheet'
import { PaneBody } from '@/features/jobs/details/components/JobSections/Pane'
import { useObservableFocus } from '@/hooks/focus-hooks'
import type { Id } from '@/infra/id'
import styles from './NotesPane.module.css'

export interface NotesPaneProps {
  isHighlighted: boolean
  canWrite: boolean
  composer: ReactNode
  isMovedNoticeShowing: boolean
  onCloseMovedNotice: () => unknown
  deleteConfirmSheet: ReactNode
  children: ReactNode
}

export function NotesPane({
  isHighlighted,
  canWrite,
  composer,
  isMovedNoticeShowing,
  onCloseMovedNotice,
  deleteConfirmSheet,
  children,
}: NotesPaneProps) {
  const cardRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (isHighlighted) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [isHighlighted])

  return (
    <>
      <PaneBody>
        <div
          ref={cardRef}
          className={cx(styles.cardWrap, isHighlighted && styles.highlighted)}
        >
          {canWrite && composer}
          {children}
        </div>
      </PaneBody>

      <ModalSheet
        showing={isMovedNoticeShowing}
        onRequestClose={onCloseMovedNotice}
      >
        <ModalSheetHeader withCloseButton />
        <ModalSheetContent>
          <ModalSheetContentBlock>
            <ModalSheetContentTitle>
              Notes now have their own section
            </ModalSheetContentTitle>
            <ModalSheetContentDescription>
              Your notes live in the Notes section of the job now. Anyone with
              access to the job can see them there, except the client.
            </ModalSheetContentDescription>
          </ModalSheetContentBlock>
        </ModalSheetContent>
        <ModalSheetFooter>
          <ModalSheetFooterSpacer />
          <ModalSheetFooterButtonGroup>
            <Button
              label="Got it!"
              variant="primary"
              onPress={onCloseMovedNotice}
            />
          </ModalSheetFooterButtonGroup>
        </ModalSheetFooter>
      </ModalSheet>

      {deleteConfirmSheet}
    </>
  )
}

export interface NotesPaneListProps {
  isEmpty: boolean
  children: ReactNode
}

export function NotesPaneList({ isEmpty, children }: NotesPaneListProps) {
  return (
    <div className={styles.list}>
      {children}
      {isEmpty && (
        <span className={styles.empty}>No notes on this job yet.</span>
      )}
    </div>
  )
}

export function NotesPaneDeleteConfirmContent() {
  return (
    <ModalSheetContentBlock>
      <ModalSheetContentTitle>Delete this note?</ModalSheetContentTitle>
      <ModalSheetContentDescription>
        This note will be removed for your whole team. This can't be undone.
      </ModalSheetContentDescription>
    </ModalSheetContentBlock>
  )
}

// Counting down only near the end keeps the composer quiet for normal notes.
const SHOW_COUNT_BELOW = 200

export interface NoteComposerRecipient {
  userPublicId: Id
  name: string
  role: string
  selected: boolean
}

export interface NoteComposerProps {
  text: string
  onTextChange: (text: string) => unknown
  maxLength: number
  charsRemaining: number
  recipients: NoteComposerRecipient[]
  onToggleRecipient: (userPublicId: Id, selected: boolean) => unknown
  canSend: boolean
  isSendPending: boolean
  onSend: () => unknown
  focus$: Observable<unknown>
}

/**
 * Writes a note in place, with who to notify and the note's visibility.
 */
export function NoteComposer({
  text,
  onTextChange,
  maxLength,
  charsRemaining,
  recipients,
  onToggleRecipient,
  canSend,
  isSendPending,
  onSend,
  focus$,
}: NoteComposerProps) {
  const inputRef = useRef<HTMLTextAreaElement | null>(null)

  useObservableFocus(focus$, () => {
    inputRef.current?.focus()
    inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  })

  return (
    <div className={styles.compose}>
      <AutosizeTextarea
        ref={inputRef}
        className={styles.input}
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canSend) {
            e.preventDefault()
            onSend()
          }
        }}
        placeholder="Leave a note for the team…"
        aria-label="Note"
        maxLength={maxLength}
        minRows={3}
        maxRows={14}
        disabled={isSendPending}
        data-private
        data-test="job-notes-input"
      />
      {recipients.length > 0 && (
        <div className={styles.notify}>
          <span className={styles.notifyLabel}>Notify</span>
          {recipients.map((recipient) => (
            <RawButton
              key={recipient.userPublicId}
              className={cx(styles.chip, recipient.selected && styles.chipOn)}
              onPress={() =>
                onToggleRecipient(recipient.userPublicId, !recipient.selected)
              }
              aria-pressed={recipient.selected}
              testId={`job-notes-notify-${recipient.userPublicId}`}
            >
              {recipient.name}
              <span className={styles.chipRole}>{recipient.role}</span>
            </RawButton>
          ))}
        </div>
      )}
      <div className={styles.composeFooter}>
        <span className={styles.visibility} data-test="job-notes-title">
          <Icon name="lock-outline" size={12} aria-hidden="true" />
          Visible to everyone on this job · never to the client
        </span>
        {charsRemaining < SHOW_COUNT_BELOW && (
          <span className={styles.count}>{charsRemaining}</span>
        )}
        <Button
          label="Add note"
          variant="primary"
          size="small"
          isDisabled={!canSend}
          isPending={isSendPending}
          onPress={onSend}
          testId="job-notes-add"
        />
      </div>
    </div>
  )
}
